"use client";

import LoadingDog from "@/components/ui/loading-components/LoadingDog";
import {
  FETCH_MODAL_DEFAULT_Z_INDEX,
  FETCH_MODAL_LOADING_MIN_HEIGHT_CLASS,
  FETCH_MODAL_SHEET_MAX_HEIGHT,
} from "./_logic/constants";
import {
  getModalContentClassName,
  getModalPanelClassName,
  getModalSizeClasses,
  type FetchModalHeight,
  type FetchModalSize,
} from "./_logic/modal-frame";
import {
  FetchModalLayerContext,
  getParentModalZIndex,
  registerModalLayer,
  resolveStackedZIndex,
  unregisterModalLayer,
} from "./_logic/modal-layers";
import { AnimatePresence, motion, useDragControls, useReducedMotion, type PanInfo } from "framer-motion";
import { X } from "lucide-react";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils/index";
import { useIsScrollable } from "./_hooks/useIsScrollable";
import { FetchButton } from "./FetchButton";

// Layout effect in the browser (so a nested modal is lifted before its first paint), plain
// effect on the server, where layout effects do nothing and React warns about them.
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

// Stack of open modal close-handlers. The topmost (last) entry owns ESC and
// backdrop-click dismissal so stacked modals don't all close at once.
const openModalStack: Array<() => void> = [];

/**
 * Shared modal-stack helpers so non-FetchModal overlays (such as the agenda planner)
 * can take part in stack-aware Escape handling, where only the topmost registered
 * surface responds to Escape. Operates on the same stack FetchModal uses internally.
 */
export const modalStack = {
  /** Register a close handler as the new topmost modal. */
  push(onClose: () => void): void {
    openModalStack.push(onClose);
  },
  /** Remove a previously-registered close handler from the stack. */
  remove(onClose: () => void): void {
    const idx = openModalStack.lastIndexOf(onClose);
    if (idx !== -1) openModalStack.splice(idx, 1);
  },
  /** True when the given close handler is the topmost open modal. */
  isTop(onClose: () => void): boolean {
    return openModalStack[openModalStack.length - 1] === onClose;
  },
  /** Closes the topmost open modal exactly as Escape would, through its own `onClose`, so any
   *  guard it has (e.g. refusing to close mid-send) still applies. No-op when nothing is open. */
  closeTop(): void {
    openModalStack[openModalStack.length - 1]?.();
  },
};
let openScrollLockCount = 0;

/**
 * Whether any open modal currently holds the page scroll lock. While locked, the body is pinned
 * with `position: fixed`, so `window.scrollY` reads 0 and any programmatic scroll computed against
 * it is wrong. Callers (e.g. deferred anchor-scroll retries) should bail out while this is true.
 * @returns True when at least one modal has the scroll lock engaged.
 */
export const isModalScrollLocked = (): boolean => openScrollLockCount > 0;
// Snapshot of body/html style + scroll position taken when the first modal in
// the stack opens, restored when the last modal closes. iOS Safari ignores
// `overflow: hidden` on body alone, so we additionally pin the body with
// `position: fixed` offset by the current scrollY (the standard "scroll-lock"
// pattern) — and restore the scrollY on cleanup so the user lands where they were.
let savedScrollY = 0;
let previousBodyStyle: {
  overflow: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  overscrollBehavior: string;
} = { overflow: "", position: "", top: "", left: "", right: "", width: "", overscrollBehavior: "" };
let previousHtmlOverscrollBehavior = "";

const BACKDROP_INITIAL = { opacity: 0 };
const BACKDROP_ANIMATE = { opacity: 1 };
const BACKDROP_EXIT = { opacity: 0 };
const BACKDROP_TRANSITION = { duration: 0.18, ease: "easeOut" as const };

const DIALOG_INITIAL = { scale: 0.96, opacity: 0, y: 8 };
const DIALOG_ANIMATE = { scale: 1, opacity: 1, y: 0 };
const DIALOG_EXIT = { scale: 0.98, opacity: 0, y: 4 };
const DIALOG_TRANSITION = { duration: 0.18, ease: [0.32, 0.72, 0, 1] as [number, number, number, number] };

// prefers-reduced-motion: a short opacity fade only, no scale, slide or spring. Used for both the
// dialog and the sheet; exit uses the same transition as enter.
const REDUCED_INITIAL = { opacity: 0 };
const REDUCED_ANIMATE = { opacity: 1 };
const REDUCED_EXIT = { opacity: 0 };
const REDUCED_TRANSITION = { duration: 0.1, ease: "easeOut" as const };

// Mobile bottom-sheet motion (the default below `sm`): slides up from the bottom edge
// and can be flicked/dragged back down to dismiss. `dragElastic.bottom: 1` lets it follow the
// finger 1:1 downward while `dragConstraints` snap it back to rest if the drag is too small.
const SHEET_INITIAL = { y: "100%" };
const SHEET_ANIMATE = { y: 0 };
const SHEET_EXIT = { y: "100%" };
const SHEET_TRANSITION = { type: "spring" as const, damping: 34, stiffness: 330 };
const SHEET_DRAG_CONSTRAINTS = { top: 0, bottom: 0 };
const SHEET_DRAG_ELASTIC = { top: 0, bottom: 1 };
// Drag distance (px) or downward flick velocity (px/s) past which the sheet dismisses.
const SHEET_DISMISS_OFFSET = 120;
const SHEET_DISMISS_VELOCITY = 600;

// Tailwind `sm` breakpoint. Below this the modal renders as a mobile bottom sheet
// sized to `100dvh`; auto-focusing the first field here pops the on-screen keyboard,
// which collapses `100dvh` and slides the field out from under the user's finger — so
// we skip auto-focus at this width.
const MOBILE_MEDIA_QUERY = "(max-width: 639px)";

/** Whether the viewport is below the `sm` breakpoint right now; false on the server. */
const isMobileViewport = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_MEDIA_QUERY).matches;

/** A field that should take focus on open says so here; React never writes `autoFocus` to the DOM. */
const AUTOFOCUS_SELECTOR = "[data-autofocus]";

const FOCUSABLE_SELECTOR = [
  "[autofocus]",
  'input:not([type="hidden"]):not([disabled])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "[href]",
  "iframe",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

interface FetchModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Accessible name for the dialog. Required when `title` is non-string JSX so screen readers don't fall back to "Dialog". */
  ariaLabel?: string;
  /** Id of an element that describes the dialog, announced with its name when it opens. */
  ariaDescribedBy?: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  topBar?: React.ReactNode;
  bottomBar?: React.ReactNode;
  className?: string;
  /**
   * Additional classes for the content wrapper, applied last (e.g. `overflow-visible!`). The
   * wrapper scrolls vertically by default. Passing your own padding or margin classes here turns
   * off the built-in focus-ring room, since you are managing that space yourself.
   */
  contentClassName?: string;
  isLoading?: boolean;
  /**
   * Backdrop layer; the panel renders one above it. A modal opened on top of another FetchModal
   * is never rendered under it: if this value is below the parent's, it is lifted above the
   * parent (see `FETCH_MODAL_STACK_Z_STEP`).
   */
  zIndex?: number;
  /** Width preset. `"xs"` (448px) suits short forms and confirms. Default `"md"`. */
  size?: FetchModalSize;
  /**
   * Height of the `"xs"`, `"sm"`, `"md"`, `"lg"` and `"xl"` presets. `"fit"` (default) hugs the
   * content up to the preset's max height, then the content scrolls inside while the header and
   * bottom bar stay put. `"fill"` always stretches to the max height: use it for tabbed or async
   * content that should not resize while the modal is open, or children that fill with `h-full`.
   * `"auto"` and `"full"` sizes ignore it.
   */
  height?: FetchModalHeight;
  /** Render the close button absolutely in the top-right corner so it floats over content and takes no layout space. */
  floatingClose?: boolean;
  /** Suppress the close button entirely (e.g. blocking flows the user must complete). */
  hideClose?: boolean;
  /**
   * Whether a backdrop click dismisses. Default true, which is right for almost
   * everything: the backdrop is the reflex for "get this out of my way". Set
   * false where dismissing costs something the visitor cannot get back, so the
   * close button and Escape stay the deliberate ways out.
   */
  dismissOnBackdrop?: boolean;
  /**
   * On mobile (<640px), render as a full-height bottom sheet with rounded top corners, a grab
   * handle and swipe-down to dismiss. Default true. Pass false to keep the dialog look on mobile.
   * `size="full"` modals are already full screen and never become a sheet.
   */
  mobileSheet?: boolean;
  /**
   * Forces the mobile sheet on, even with `mobileSheet={false}` or `mobilePlacement="top"`. Kept
   * for callers written before the sheet became the default.
   */
  swipeToDismiss?: boolean;
  /**
   * On mobile (<640px), align the dialog to the viewport top instead of the default bottom
   * placement. A top-anchored dialog cannot be a bottom sheet, so `"top"` also turns the default
   * sheet off (unless `swipeToDismiss` is set).
   */
  mobilePlacement?: "bottom" | "top";
  /**
   * On desktop (>=640px), center the dialog (default) or pin it near the viewport top. Use `"top"`
   * for content that grows or shrinks while open (expandable rows), so the header stays put and
   * only the bottom edge moves instead of the whole dialog jumping.
   */
  desktopPlacement?: "center" | "top";
}

/**
 * Reusable modal: backdrop, enter/exit animations, scroll-lock, focus trapping,
 * ESC/backdrop dismissal, and stacking support. Renders into a portal on `document.body`.
 *
 * Behavior (each one changed on purpose from the earlier version):
 * - Height: preset sizes hug their content up to the max height (`height="fit"`); pass
 *   `height="fill"` for the old always-tall frame. The content wrapper is the scroller.
 * - Focus rings: the content wrapper has symmetric ring room, so rings are not clipped.
 * - Mobile: below 640px the modal is a full-height bottom sheet by default (`mobileSheet`).
 * - Stacking: a modal opened from another FetchModal is never rendered underneath it.
 * - Frame: hairline border and soft neutral shadow like the admin overlays; no backdrop blur.
 * - Motion: honors prefers-reduced-motion with a short fade.
 * @param props - Modal configuration; see {@link FetchModalProps} for per-field docs.
 * @returns The modal portal element, or `null` before mount or when `document` is unavailable.
 */
const FetchModal: React.FC<FetchModalProps> = ({
  isOpen,
  onClose,
  title,
  ariaLabel,
  ariaDescribedBy,
  subtitle,
  children,
  topBar,
  bottomBar,
  className = "",
  contentClassName = "",
  isLoading,
  zIndex = FETCH_MODAL_DEFAULT_Z_INDEX,
  size = "md",
  height = "fit",
  floatingClose = false,
  hideClose = false,
  dismissOnBackdrop = true,
  mobileSheet = true,
  swipeToDismiss = false,
  mobilePlacement = "bottom",
  desktopPlacement = "center",
}) => {
  // Hooks
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Drag is initiated only from the grab handle (dragListener={false}); this keeps the sheet's
  // scrollable content from competing with the dismiss gesture.
  const dragControls = useDragControls();
  const shouldReduceMotion = useReducedMotion();

  // States
  const [isMounted, setIsMounted] = useState(false);
  const isContentScrollable = useIsScrollable(contentRef, isMounted && isOpen);
  // Tracks the <640px (Tailwind `sm`) breakpoint so the bottom sheet only applies on mobile. Read
  // up front so the first visible frame already has the right shape (the portal renders nothing
  // until mount, so the server and client first renders still match).
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  // The z-index this modal actually renders at; lifted above its parent modal when needed.
  const [stackZIndex, setStackZIndex] = useState(zIndex);

  // Derived values
  const sizeClasses = getModalSizeClasses(size, height, className);
  // When the caller supplies their own `max-h-*` class (e.g. responsive full-screen-on-mobile
  // modals using `max-h-dvh sm:max-h-[90vh]`), defer to it. The inline `maxHeight` style
  // below would otherwise win over the class and cap every breakpoint at the built-in value,
  // silently breaking full-screen mobile layouts.
  const hasCustomMaxHeight = className.includes("max-h-");
  // Only render the bottom sheet on mobile; desktop keeps the standard centered dialog.
  const wantsSheet = swipeToDismiss || (mobileSheet && mobilePlacement !== "top");
  const isSheet = isMobile && size !== "full" && wantsSheet;
  // A modal that opts out of backdrop dismissal or hides its close button must not be swiped away
  // either, unless the caller asked for swipe-to-dismiss by name.
  const canSwipeDismiss = swipeToDismiss || (dismissOnBackdrop && !hideClose);
  const panelMaxHeight = isSheet ? FETCH_MODAL_SHEET_MAX_HEIGHT : sizeClasses.maxHeight;
  const motionProps = shouldReduceMotion
    ? { initial: REDUCED_INITIAL, animate: REDUCED_ANIMATE, exit: REDUCED_EXIT, transition: REDUCED_TRANSITION }
    : isSheet
      ? { initial: SHEET_INITIAL, animate: SHEET_ANIMATE, exit: SHEET_EXIT, transition: SHEET_TRANSITION }
      : { initial: DIALOG_INITIAL, animate: DIALOG_ANIMATE, exit: DIALOG_EXIT, transition: DIALOG_TRANSITION };
  const backdropTransition = shouldReduceMotion ? REDUCED_TRANSITION : BACKDROP_TRANSITION;
  const backdropLayoutClassName = cn(
    !isSheet && mobilePlacement === "top"
      ? "items-start overflow-y-auto"
      : "items-end overflow-hidden sm:overflow-y-auto",
    desktopPlacement === "top" ? "sm:items-start" : "sm:items-center"
  );

  // The same close button is placed three ways (floating, no-title row, title
  // row); define it once and let the call site supply only placement classes.
  const renderCloseButton = (extraClassName?: string) => (
    <FetchButton
      // `FetchButton`'s `onClick` type is `() => void`, but it wires straight to the native
      // button's `onClick`, so the real click event still arrives here at runtime. Stopped
      // before it can bubble: this modal renders into a `document.body` portal, so a DOM click
      // has no ancestor to bubble through, but a React *synthetic* click still bubbles up the
      // component tree it was rendered from - straight into whatever opened this modal (e.g. a
      // table row's own onClick), one render frame after Close is clicked.
      onClick={(event?: React.MouseEvent) => {
        event?.stopPropagation();
        onClose();
      }}
      type="button"
      variant="ghost"
      color="neutral"
      size="xs"
      iconOnly
      aria-label="Close"
      className={cn("touch-manipulation", extraClassName)}
      leftIcon={<X className="w-4 h-4" />}
    />
  );

  // Handlers
  // Dismiss the sheet when dragged down far enough or flicked down quickly; otherwise the
  // drag constraints spring it back to rest automatically.
  const handleSheetDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (info.offset.y > SHEET_DISMISS_OFFSET || info.velocity.y > SHEET_DISMISS_VELOCITY) onClose();
    },
    [onClose]
  );

  // Focus trapping: keep Tab/Shift+Tab within the modal
  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab" || !modalRef.current) return;

    const focusable = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      // Focus rests on the dialog itself on mobile; Shift+Tab from there wraps to the end.
      if (document.activeElement === first || document.activeElement === modalRef.current) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }, []);

  // Effects
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Track the mobile breakpoint for the bottom-sheet behavior.
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Keeps the escape/stack-registration effect below stable across re-renders that only change
  // the `onClose` reference. Several callers (e.g. LabelModal, GeneratePaletteModal) pass an
  // inline, non-memoized `onClose` — every state change while the modal is open (including one
  // driven by something nested inside it, like a FetchColorPicker's onChange) would otherwise
  // re-run the stack effect below and re-push this modal to the TOP of the shared stack, burying
  // whatever that nested picker itself pushed when IT opened. The next Escape would then close
  // this modal (discarding whatever the user was mid-editing) instead of just the picker.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Stable identity for this modal instance's "close" on the shared stack — used by both the
  // ESC handler below and the backdrop-click handler further down, so both agree on what counts
  // as "this modal" regardless of how many times `onClose` itself has been recreated.
  const close = useCallback(() => onCloseRef.current(), []);

  // Stack-aware ESC handler. Only the topmost open modal responds to ESC,
  // so dismissing a stacked inner modal preserves the parent modal's state.
  // Registers with deps `[isOpen, close]` — `close` is stable for the component's lifetime (see
  // above), so this modal's position in the stack doesn't shift for as long as it stays
  // continuously open, even while callers pass a fresh, non-memoized `onClose` on every render.
  useEffect(() => {
    if (!isOpen) return;
    openModalStack.push(close);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const top = openModalStack[openModalStack.length - 1];
      if (top !== close) return;
      close();
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      const idx = openModalStack.lastIndexOf(close);
      if (idx !== -1) openModalStack.splice(idx, 1);
    };
  }, [isOpen, close]);

  // Depth-aware layering: a modal opened from another FetchModal resolves its z-index against
  // that parent before its first paint, so it never opens underneath it. The resolved value is
  // registered for any modal opened on top of this one, and kept through the exit animation.
  useIsomorphicLayoutEffect(() => {
    if (!isOpen) return;
    const resolved = resolveStackedZIndex(zIndex, getParentModalZIndex(openModalStack, close));
    registerModalLayer(close, resolved);
    setStackZIndex(resolved);
    return () => unregisterModalLayer(close);
  }, [isOpen, zIndex, close]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleFocusTrap);
    // Auto-focus the first focusable element — but not on mobile, where popping the keyboard
    // collapses the `100dvh` sheet. Read the media query live (rather than the `isMobile` state)
    // so a first-open before the breakpoint effect settles still gets the right answer.
    // On mobile, focus the dialog container instead so focus is still inside the dialog (keyboard
    // and screen reader users) without raising the keyboard.
    const isMobileNow = typeof window !== "undefined" && window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    requestAnimationFrame(() => {
      if (isMobileNow) {
        modalRef.current?.focus({ preventScroll: true });
        return;
      }
      const first =
        modalRef.current?.querySelector<HTMLElement>(AUTOFOCUS_SELECTOR) ??
        modalRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    });
    return () => document.removeEventListener("keydown", handleFocusTrap);
    // `isMounted` is a dependency because the portal does not exist on the first
    // commit: this effect would otherwise run once against a null ref and never
    // again. That only bites a modal rendered already open — most call sites
    // mount closed and flip, which gives the effect a second run for free — and
    // there it left focus on the page behind an `aria-modal` dialog, with the
    // trap inert because it only fires from inside.
  }, [isOpen, isMounted, handleFocusTrap]);

  // Return focus to whatever opened the modal once it closes, unless the page already moved focus
  // somewhere else (a caller that lands the user on a result after a send keeps that choice).
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (!opener?.isConnected) return;
      const focused = document.activeElement;
      const isFocusLeftInModal = focused === document.body || !!modalRef.current?.contains(focused);
      if (isFocusLeftInModal) opener.focus({ preventScroll: true });
    };
  }, [isOpen]);

  // A closing modal stays on the page through its exit animation. It is marked the way Headless UI
  // marks its own leaving dialogs, so page shortcuts can tell it from an open one.
  useEffect(() => {
    if (!isOpen) modalRef.current?.setAttribute("data-leave", "");
  }, [isOpen]);

  // Lock page scroll while open so the background can't scroll behind the modal.
  // iOS Safari needs `position: fixed` on body (overflow:hidden alone leaks);
  // we save the current scrollY and restore it on cleanup so the user lands
  // back in the same place. Stack-refcounted so nested modals don't double-lock
  // or restore the wrong scroll position.
  useEffect(() => {
    if (!isOpen) return;

    const { body, documentElement } = document;

    if (openScrollLockCount === 0) {
      savedScrollY = window.scrollY;
      previousBodyStyle = {
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overscrollBehavior: body.style.overscrollBehavior,
      };
      previousHtmlOverscrollBehavior = documentElement.style.overscrollBehavior;
      body.style.overflow = "hidden";
      body.style.overscrollBehavior = "none";
      body.style.position = "fixed";
      body.style.top = `-${savedScrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
      documentElement.style.overscrollBehavior = "none";
    }

    openScrollLockCount += 1;

    return () => {
      openScrollLockCount = Math.max(0, openScrollLockCount - 1);
      if (openScrollLockCount > 0) return;

      body.style.overflow = previousBodyStyle.overflow;
      body.style.overscrollBehavior = previousBodyStyle.overscrollBehavior;
      body.style.position = previousBodyStyle.position;
      body.style.top = previousBodyStyle.top;
      body.style.left = previousBodyStyle.left;
      body.style.right = previousBodyStyle.right;
      body.style.width = previousBodyStyle.width;
      documentElement.style.overscrollBehavior = previousHtmlOverscrollBehavior;
      // Restore scrollY synchronously so the page doesn't flash to the top
      // before the user's eye notices. `scrollTo` with `instant` avoids the
      // smooth-scroll behavior some pages set globally.
      window.scrollTo({ top: savedScrollY, left: 0, behavior: "instant" as ScrollBehavior });
    };
  }, [isOpen]);

  // Render
  // The layer context covers the header, content and bottom bar, so any portaled popover in the
  // modal can read the modal's layer through useModalLayerZIndex.
  const modalContent = (
    <FetchModalLayerContext.Provider value={stackZIndex + 1}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={BACKDROP_INITIAL}
            animate={BACKDROP_ANIMATE}
            exit={BACKDROP_EXIT}
            transition={backdropTransition}
            className={cn("fixed inset-0 flex justify-center bg-black/50", backdropLayoutClassName)}
            style={{ zIndex: stackZIndex }}
            onClick={(e) => {
              if (!dismissOnBackdrop) return;
              if (e.target !== e.currentTarget) return;
              // Only the topmost modal dismisses on backdrop click.
              const top = openModalStack[openModalStack.length - 1];
              if (top && top !== close) return;
              // Same React-synthetic-bubbling reason as the close button above - this backdrop
              // click must not also reach whatever opened this modal.
              e.stopPropagation();
              close();
            }}
          >
            <motion.div
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel ?? (typeof title === "string" ? title : "Dialog")}
              aria-describedby={ariaDescribedBy}
              // Programmatic focus target on mobile (see the focus effect); not a tab stop.
              tabIndex={-1}
              {...motionProps}
              drag={isSheet && canSwipeDismiss ? "y" : false}
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={isSheet ? SHEET_DRAG_CONSTRAINTS : undefined}
              dragElastic={isSheet ? SHEET_DRAG_ELASTIC : undefined}
              onDragEnd={isSheet ? handleSheetDragEnd : undefined}
              className={cn(getModalPanelClassName({ size, sizeClasses, isSheet, className }), "focus:outline-none")}
              style={{ zIndex: stackZIndex + 1, ...(hasCustomMaxHeight ? {} : { maxHeight: panelMaxHeight }) }}
            >
              {/* Grab handle: the only drag-initiating surface (dragListener is off), so the
                sheet dismisses on a handle drag but content still scrolls normally. */}
              {isSheet && canSwipeDismiss && (
                <div
                  data-slot="grab-handle"
                  onPointerDown={(event) => dragControls.start(event)}
                  className="-mt-2 mb-1 flex shrink-0 touch-none cursor-grab justify-center py-2 active:cursor-grabbing"
                >
                  <span className="h-1.5 w-10 rounded-full bg-gray-300" aria-hidden="true" />
                </div>
              )}

              {/* Floating close button. Absolutely positioned over the top-right
                corner so it takes no layout space and overlaps content. The
                wrapping div carries the `absolute` (FetchButton's baseClasses
                force `position: relative`, so the button itself can't be
                absolute via className). */}
              {floatingClose && !hideClose && <div className="absolute top-4 right-4 z-10">{renderCloseButton()}</div>}

              {/* No-title close-button row. Renders as a normal flex row (not
                absolute) so the button sizes itself naturally instead of
                stretching across the flex-column modal — FetchButton's
                baseClasses include `position: relative`, which previously
                won over an `absolute` override applied via className. */}
              {!title && !topBar && !floatingClose && !hideClose && (
                <div className="flex justify-end items-center mb-2 shrink-0 -mr-2 -mt-2">{renderCloseButton()}</div>
              )}

              {/* Top Bar */}
              {(title || topBar) && (
                <div className="flex items-center justify-between mb-4 shrink-0">
                  {title && (
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 wrap-break-word">{title}</h3>
                      {subtitle && <div className="text-sm font-medium text-gray-600 mt-1">{subtitle}</div>}
                    </div>
                  )}
                  {topBar && <div className="flex items-center gap-2">{topBar}</div>}
                  {/* Pinned to the top so a wrapped title keeps the X beside its first line. */}
                  {title && !floatingClose && !hideClose && renderCloseButton("ml-4 self-start")}
                </div>
              )}

              {/* Content */}
              <div
                ref={contentRef}
                // Only a body that actually scrolls joins the tab order, so a keyboard user can scroll it.
                tabIndex={isContentScrollable ? 0 : undefined}
                className={cn(
                  getModalContentClassName(contentClassName, className),
                  isContentScrollable &&
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
                )}
                style={{
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  touchAction: "pan-y",
                }}
              >
                {isLoading ? (
                  <div className={cn("flex items-center justify-center h-full", FETCH_MODAL_LOADING_MIN_HEIGHT_CLASS)}>
                    <LoadingDog size={16} />
                  </div>
                ) : (
                  children
                )}
              </div>

              {/* Bottom Bar: divider extends edge-to-edge of the modal frame
                via negative horizontal margins that cancel the modal's p-6,
                then `px-6` re-insets the bottomBar content to its natural
                column. */}
              {bottomBar && <div className="shrink-0 mt-6 -mx-6 px-6 pt-4 border-t border-gray-100">{bottomBar}</div>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </FetchModalLayerContext.Provider>
  );

  if (!isMounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default FetchModal;
